import deepmerge from 'deepmerge';
import {
  BrandId,
  brandSpecific,
  localeSpecificGroup,
  LocaleSpecificGroup,
} from '../override';
import {
  BRAND_SPECIFIC_FLAG_KEY,
  BRAND_SPECIFIC_FLAG_VALUE,
  BrandSpecificWithFlag,
} from './staticText';

export type TranslContext = {
  readonly brand: BrandId;
  readonly lang: string;
};

const resolveLocaleSpecificGroup = <T>(
  group: LocaleSpecificGroup<T> | BrandSpecificWithFlag<LocaleSpecificGroup<T>>,
  context: TranslContext,
): LocaleSpecificGroup<T> => {
  if (
    BRAND_SPECIFIC_FLAG_KEY in group &&
    group[BRAND_SPECIFIC_FLAG_KEY] === BRAND_SPECIFIC_FLAG_VALUE
  ) {
    const brandSpecificGroup = brandSpecific(group).get(context.brand);
    return deepmerge<LocaleSpecificGroup<T>>(group._, brandSpecificGroup ?? {});
  } else {
    return group as LocaleSpecificGroup<T>;
  }
};

// Resolved translations are cached by (group ref, brand:lang). Translation
// dictionaries are module-level singletons, so the group reference is stable
// and WeakMap allows GC if a dictionary is ever discarded. The inner Map is
// bounded by brand × locale (4 × ~6 = at most 24 entries per group).
//
// Without this cache, every call repeats deepmerge (when group has the brand
// flag) + a full Object.entries iteration in localeSpecificGroup().get(),
// allocating a new result object. Hot on hydration when many components
// resolve translations from the same dictionary on the same page.
const translationCache = new WeakMap<object, Map<string, unknown>>();

export function getClientTranslations<T>(
  group: LocaleSpecificGroup<T> | BrandSpecificWithFlag<LocaleSpecificGroup<T>>,
  context: TranslContext,
): T {
  const cacheKey = `${context.brand}:${context.lang}`;
  const groupCache = translationCache.get(group);
  if (groupCache) {
    const cached = groupCache.get(cacheKey);
    if (cached !== undefined) return cached as T;
  }

  const keys = resolveLocaleSpecificGroup(group, context);
  const staticText = localeSpecificGroup(keys).get(context.lang);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- sneaky patch
  (staticText as any).toJSON = () => {
    // Throw an error when all keys are being sent to the client (experimental_taintObjectReference)
    throw new Error(
      'Do not pass all static keys to the client. Use filterKeys.',
    );
  };

  const perGroup = groupCache ?? new Map<string, unknown>();
  perGroup.set(cacheKey, staticText);
  if (!groupCache) translationCache.set(group, perGroup);

  return staticText;
}
